import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Avatar, Box, Tooltip, Typography, } from "@mui/material";
import RichTextEditor from "../Common/RichTextEditor";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import ProgressSpinner from "../Common/ProgressSpinner";
import { getUserAttributes } from "../../utils/auth";
import ActionItemNotesService from "../../services/actionItems/ActionItemNotes";
import EditIcon from "../../assets/action-items/comments-edit.svg";
import DeleteIcon from "../../assets/action-items/slideOver-delete.svg";
import CustomConfirmation from "../Common/CustomConfirmation";
import { dateFormatter, getAvatarColor, getAvatarInitials } from "../../utils/utils";

const ActionItemNotes: React.FC<{
  actionItemNotes: any[];
  setActionItemNotes: React.Dispatch<React.SetStateAction<any[]>>;
  isPreview?: boolean;
  newActionItemNote?: string | undefined;
  setNewActionItemNote: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  hideMenu?: boolean;
  setHideMenu?: React.Dispatch<React.SetStateAction<boolean>>;
  selectedActionItem?: any;
  isNewActionItem?: boolean;
  activeUsers?: any;
}> = ({
  actionItemNotes,
  setActionItemNotes,
  newActionItemNote,
  setNewActionItemNote,
  hideMenu = false,
  setHideMenu,
  selectedActionItem,
  isNewActionItem,
  activeUsers,
}) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useDisplaySnackbar();
  const loggedInEmail = getUserAttributes()?.email;
  const pictureUrl = getUserAttributes()?.profilePicture;
  const [selectedNoteId, setSelectedNoteId] = useState("");
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const originalNoteContentRef = useRef<Record<string, string>>({});
  const composerEditorRef = useRef<any>(null);
  const editingEditorRefs = useRef<Record<string, any>>({});
  const normalizeEditorHtml = (html?: string) => {
    const raw = (html || "")
      .replace(/&nbsp;/gi, " ")
      .replace(/\u00a0/g, " ");
    const div = document.createElement("div");
    div.innerHTML = raw;
    return (div.textContent || "").replace(/\s+/g, " ").trim();
  };

  const cancelEditingNote = (noteId: string) => {
    const originalContent = originalNoteContentRef.current[noteId];
    if (typeof originalContent === "string") {
      setActionItemNotes((prev) =>
        prev.map((note) =>
          note.id === noteId ? { ...note, notes: originalContent, isEdited: false } : note
        )
      );
      delete originalNoteContentRef.current[noteId];
    }
    setEditingNoteId(null);
    setActiveMenuId(null);
    setHideMenu?.(true);
  };

  const extractMentions = (html: string) => {
    if (!html) return [] as { id: string; name?: string }[];

    const div = document.createElement("div");
    div.innerHTML = html;

    return Array.from(div.querySelectorAll(".mention[data-mention-id]"))
      .map((el) => ({
        id:
          el.getAttribute("data-mention-id"),
        name:
          el.getAttribute("label") ||
          el.textContent?.replace(/^@/, "") ||
          undefined,
      }))
      .filter(m => m.id);
  };

  const extractMentionIdsFromEditor = (editor: any) => {
    if (!editor?.getJSON) {
      return [] as string[];
    }

    const mentionIds = new Set<string>();
    const visitNode = (node: any) => {
      if (!node || typeof node !== "object") return;

      if (node.type === "mention" && node.attrs?.id) {
        mentionIds.add(String(node.attrs.id));
      }

      if (Array.isArray(node.content)) {
        node.content.forEach(visitNode);
      }
    };

    visitNode(editor.getJSON());
    return Array.from(mentionIds);
  };

  const { mutate: mutateCreateActionItemNote } =
  ActionItemNotesService.useCreateActionItemNote(selectedActionItem?.id || "", {
    onMutate: () => setShowSpinner(true),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["get_action_item_comments", selectedActionItem?.id],
      });
      setNewActionItemNote("");
      setIsComposerOpen(false);
      showSnackbar("success", "Comment added successfully");
    },
    onError: (error: string) =>
      showSnackbar("error", `Error adding comment: ${error}`),
    onSettled: () => setShowSpinner(false),
  });
  
  const { mutate: mutateUpdateActionItemNote } =
  ActionItemNotesService.useUpdateActionItemNote(
    selectedActionItem?.id || "",
    {
      onMutate: () => setShowSpinner(true),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["get_action_item_comments", selectedActionItem?.id],
        });
        showSnackbar("success", "Comment updated successfully");
      },
      onError: (error: string) =>
        showSnackbar("error", `Error updating comment: ${error}`),
      onSettled: () => setShowSpinner(false),
    }
  );

  const { mutate: mutateMeetingMinuteDelete } =
    ActionItemNotesService.useDeleteActionItemNote(selectedNoteId, {
      onMutate: () => {
        setShowSpinner(true);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["get_action_item_comments"],
        });
        handleClose();
        showSnackbar("success", "Task note deleted successfully");
      },
      onError: (error: string) => {
        showSnackbar(
          "error",
          `Error while deleting task note: ${error}`
        );
      },
      onSettled: () => {
        setShowSpinner(false);
      },
    });

  const deleteActionItemNote = () => {
    mutateMeetingMinuteDelete({ noteId: selectedNoteId });
  };

  const handleClose = () => {
    setOpenDialog(false);
    setSelectedNoteId("");
  };
  const resetComposer = () => {
    setIsComposerOpen(false);
    setNewActionItemNote("");
    setActiveMenuId(null);
    setEditingNoteId(null);
    setSelectedNoteId("");
    setOpenDialog(false);
    originalNoteContentRef.current = {};
    setHideMenu?.(true);
  };

  useEffect(() => {
    resetComposer();
  }, [selectedActionItem?.id]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as HTMLElement;
      const hasComposerText = normalizeEditorHtml(
        composerEditorRef.current?.getHTML?.() || newActionItemNote || ""
      ).length > 0;

      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node) && 
      !target.closest(".action-item-save-button") && 
      !target.closest(".action-item-cancel-button") &&
        !target.closest(".editor-toolbar") &&
        !target.closest(".tippy-box") &&           // ✅ allow mention popup
        !target.closest(".tippy-content") &&       // ✅ sometimes needed
        !target.closest(".mention-list")   
      ) {
        if (editingNoteId) {
          cancelEditingNote(editingNoteId);
          return;
        }

        if (isComposerOpen) {
          setActiveMenuId(null);
          setHideMenu?.(true);

          if (!hasComposerText) {
            setIsComposerOpen(false);
          }
        }
      }
    }
  
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [editingNoteId, isComposerOpen, newActionItemNote]);

  const normalizedActiveUsers = useMemo(() => {
    return (activeUsers ?? []).map((u: any) => ({
      id: u.profileOrgRelationshipId, // stable + unique
      name: u.name,
      email: u.email,
      pictureUrl: u.pictureUrl,
    }));
  }, [activeUsers]);

  return (
    <>
        <Box ref={wrapperRef}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: "10px",
              marginBottom: "20px",
            }}
          >
            <Tooltip title={loggedInEmail} arrow placement="top">
              <Avatar
                src={pictureUrl || undefined}
                sx={{ 
                  width: 32, 
                  height: 32,
                  bgcolor: !pictureUrl ? getAvatarColor(loggedInEmail) : undefined,
                }}
              >
                {!pictureUrl && getAvatarInitials(loggedInEmail)}
              </Avatar>
            </Tooltip>
            <Box
              className="add_comment_box"
              sx={{ border: isComposerOpen ? "1px solid #7F7F7F":"1px solid #E5E7EB", }}
            >
              {/* COLLAPSED STATE */}
              {!isComposerOpen && (
                <Box
                  onClick={() => {
                    setIsComposerOpen(true);
                    setHideMenu?.(false);
                  }}
                  sx={{ padding: "10px 12px", cursor: "pointer", color: "#9595A3"}}
                >
                  Add a comment…
                </Box>
              )}
              {isComposerOpen && (
              <RichTextEditor
                key="add_new_note"
                editorRef={(editor) => {
                  composerEditorRef.current = editor;
                }}
                content={isNewActionItem ? "" : (newActionItemNote || null)}
                clearContent={newActionItemNote === ""}
                onContentUpdate={setNewActionItemNote}
                hideMenu={hideMenu}
                setHideMenu={setHideMenu}
                hideCopyButton={true}
                parentPage="actionItemNotes"
                activeUsers={normalizedActiveUsers}
                showFooter={true}
                autoFocus={true}
                isDefaultHidingBorder={true}
                footer={
                <>
                  <button
                    className="btn btn-outlined-small"
                    onClick={() => {
                      setNewActionItemNote("");
                      setIsComposerOpen(false);
                      setHideMenu?.(true);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className={`btn btn-purple-compact ${!newActionItemNote || newActionItemNote.trim() === "" || isNewActionItem ? "disabled-look" : ""}`}
                    onClick={() => {
                      const latestCommentHtml =
                        composerEditorRef.current?.getHTML?.() || newActionItemNote || "";
                      const mentionedPorIdsFromEditor = extractMentionIdsFromEditor(composerEditorRef.current);
                      const mentionedPorIds =
                        mentionedPorIdsFromEditor.length > 0
                          ? mentionedPorIdsFromEditor
                          : extractMentions(latestCommentHtml).map((m) => m.id);
                      mutateCreateActionItemNote({
                        comment: latestCommentHtml,
                        commentType: "TEXT",
                        isForUpdate: false,
                        actionItemId: selectedActionItem?.id,
                        mentionedPorIds,
                        actionItemTitle: selectedActionItem?.description,
                      })
                      setIsComposerOpen(false);
                    }
                    }
                    disabled={!newActionItemNote || newActionItemNote.trim() === "" || isNewActionItem}
                  > Save </button>
                </>
                }
              />
              )}
            </Box>
          </Box>

          {/* Existing Comments */}
          {actionItemNotes?.map((item) => {
            const isEditing = editingNoteId === item.id;
            const canEdit = loggedInEmail === item.creatorEmail;
            const originalNoteContent =
              originalNoteContentRef.current[item.id] ?? item.notes ?? "";
            const normalizedCurrent = normalizeEditorHtml(item.notes);
            const normalizedOriginal = normalizeEditorHtml(originalNoteContent);

            const isEditedInSession = normalizedCurrent !== normalizedOriginal;
            const isEmpty = normalizedCurrent.length === 0;

            return (
              <Box
                key={item.id}
                sx={{
                  border: isEditing ? "1px solid #7F7F7F" : "1px solid #E5E7EB",
                  borderRadius: "10px",
                  padding: "12px",
                  paddingBottom: "2px",
                  marginBottom: "12px",
                  backgroundColor: "#FFFFF",
                  marginLeft: "40px",
                }}
              >
                {/* HEADER */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Avatar
                      src={item.creatorPictureUrl || undefined}
                      sx={{ 
                        width: 24, 
                        height: 24,
                        bgcolor: !item.creatorPictureUrl ? getAvatarColor(item.creatorEmail || item.creatorName || "") : undefined,
                      }}
                    >
                      {!item.creatorPictureUrl && getAvatarInitials(item.creatorEmail)}
                    </Avatar>

                    <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#374151" }}>
                      {item.creatorName || item.creatorEmail}
                    </Typography>
                    <Typography sx={{ fontWeight: 500, fontSize: "10px", color: "#7A7A8C" }}>
                      {dateFormatter(item.updatedAt)}                   
                    </Typography>
                  </Box>

                  {canEdit && !isEditing && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <Tooltip title="Edit" arrow placement="top">
                      <button
                        className="icon-button"
                        onClick={() => {
                          originalNoteContentRef.current[item.id] = item.notes || "";
                          setEditingNoteId(item.id);
                          setActiveMenuId(item.id);
                          setHideMenu?.(false);
                        }}
                      >
                        <img src={EditIcon} alt="Edit" style={{ width: "18px", height: "18px" }} />
                      </button>
                      </Tooltip>
                      <Tooltip title="Delete" arrow placement="top">
                      <button
                        className="icon-button"
                        onClick={() => {
                          setSelectedNoteId(item.id);
                          setOpenDialog(true);
                        }}
                      >
                       <img src={DeleteIcon} alt="Delete" style={{ width: "14px", height: "14px" }} />
                      </button>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
                {!isEditing ? (
                  <div
                    className="actionItemNotesTextEditor"
                    style={{borderRadius: "8px", padding: "8px", fontSize: "13px", paddingLeft: "12px", maxHeight: "150px", overflowY: "auto" }}
                    dangerouslySetInnerHTML={{ __html: item.notes }}
                  />
                ) : (
                <Box>
                <RichTextEditor
                  key={item.id}
                  editorRef={(editor) => {
                    if (editor) {
                      editingEditorRefs.current[item.id] = editor;
                    } else {
                      delete editingEditorRefs.current[item.id];
                    }
                  }}
                  content={item.notes}
                  onContentUpdate={(documentHTMLContent) => {
                    setActionItemNotes((prev) =>
                      prev.map((note) =>
                        note.id === item.id ? { ...note, notes: documentHTMLContent, isEdited: true } : note
                      )
                    );
                  }}
                  isEditable={loggedInEmail === item.creatorEmail}
                  hideMenu={activeMenuId !== item.id}
                  isActive={false}
                  isDefaultHidingBorder={true}
                  hideCopyButton={true}
                  activeUsers={normalizedActiveUsers}
                  showFooter={true}
                  parentPage="actionItemNotes"
                  autoFocus={true}
                  footer={
                    <>
                    <button
                      className="btn btn-outlined-small"
                      onClick={() => cancelEditingNote(item.id)}
                    >
                      Cancel
                    </button>
                    <button
                      className={`btn btn-purple-compact ${
                        !isEditedInSession || isEmpty ? "disabled-look" : ""
                      }`}
                      disabled={!isEditedInSession || isEmpty}
                      onClick={() => {
                        const latestCommentHtml =
                          editingEditorRefs.current[item.id]?.getHTML?.() || item.notes;
                        const mentionedPorIdsFromEditor = extractMentionIdsFromEditor(
                          editingEditorRefs.current[item.id]
                        );
                        const mentionedPorIds =
                          mentionedPorIdsFromEditor.length > 0
                            ? mentionedPorIdsFromEditor
                            : extractMentions(latestCommentHtml).map((m) => m.id);
                        mutateUpdateActionItemNote({
                          comment: latestCommentHtml,
                          commentType: "TEXT",
                          isForUpdate: true,
                          commentId: item.id,
                          actionItemId: selectedActionItem?.id,
                          mentionedPorIds, 
                          actionItemTitle: selectedActionItem?.description,
                        });
                        setEditingNoteId(null);
                        setActiveMenuId(null);
                      }}
                    >
                      Save
                    </button>
                    </>
                  }
                  />
                </Box>
                )}
              </Box>
            );
          })}
        </Box>

      <CustomConfirmation
        title="Are you sure you want to delete this comment?"
        width="350px"
        actionBtnLabel="Delete"
        cancelBtnLabel="Cancel"
        handleSuccess={deleteActionItemNote}
        handleClose={() => handleClose()}
        backgroundcolor={false}
        isOpen={openDialog}      
      />
      <ProgressSpinner showSpinner={showSpinner} />
    </>
  );
};

export default ActionItemNotes;
